import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { blogPosts } from '../data/mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';
import { Toaster } from '../components/ui/toaster';

const BlogPost = () => {
  const { slug } = useParams();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const openForm = () => setIsContactFormOpen(true);
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#030308]">
        <Header onOpenContactForm={openForm} />
        <div className="flex items-center justify-center pt-40 pb-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Article Not Found</h1>
            <p className="text-slate-400 mb-6">The blog post you are looking for does not exist.</p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-md uppercase tracking-wider">Back to Home</Button>
            </Link>
          </div>
        </div>
        <Footer onOpenContactForm={openForm} />
        <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
        <Toaster />
      </div>
    );
  }

  const otherPosts = blogPosts.filter(p => p.id !== post.id);

  return (
    <div className="min-h-screen bg-[#030308]">
      <Header onOpenContactForm={openForm} />

      {/* Hero image */}
      <div className="relative h-64 md:h-96 overflow-hidden pt-28">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030308] via-[#030308]/60 to-[#030308]/40" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-8">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block bg-indigo-500/80 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-md mb-3">
              {post.category}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 data-testid="blog-post-title" className="text-3xl sm:text-4xl font-black tracking-tight text-white mb-4 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {post.title}
        </h1>

        <div className="flex items-center gap-4 mb-10 pb-8 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-500">{post.readTime}</span>
          </div>
          <span className="text-slate-600">|</span>
          <span className="text-sm text-slate-500">By Trademark My LLC</span>
        </div>

        <div className="space-y-6">
          {post.content.map((block, index) => {
            if (block.type === 'heading') {
              return (
                <h2 key={index} className="text-xl font-bold text-white mt-10 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {block.text}
                </h2>
              );
            }
            return (
              <p key={index} className="text-base text-slate-300 leading-relaxed">
                {block.text}
              </p>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-14 p-8 rounded-xl bg-[#0A0A12] border border-white/[0.06] text-center">
          <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Ready to Protect Your Brand?</h3>
          <p className="text-sm text-slate-400 mb-5">Get expert trademark registration starting at just $45.</p>
          <Button
            onClick={openForm}
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold px-8 py-5 text-sm rounded-md uppercase tracking-wider btn-shine shadow-lg shadow-amber-500/20"
          >
            Start My Registration
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>

        {/* Related articles */}
        {otherPosts.length > 0 && (
          <div className="mt-16 pt-10 border-t border-white/10">
            <h3 className="text-lg font-bold text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>More Resources</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {otherPosts.map(p => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-post-${p.id}`} className="group flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-amber-500/30 transition-all">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] text-amber-500 font-bold uppercase tracking-wider">{p.category}</span>
                    <h4 className="text-sm font-bold text-white group-hover:text-amber-400 transition-colors leading-snug mt-0.5">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer onOpenContactForm={openForm} />
      <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
      <Toaster />
    </div>
  );
};

export default BlogPost;
